/**
 * Notification bell popover in the app header.
 *
 * Shows an unread count badge, a paginated list of notifications, and
 * "Mark as read" controls. Clicking a notification navigates to the
 * relevant page and marks it as read in one action.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Bell, Check, CheckCheck } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { T, RADIUS, SHADOW } from '@/lib/tokens';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationCount,
  useNotifications,
  NotificationItem,
} from '@/hooks/useNotifications';
import { ApproveLoanRequestDialog } from '@/components/ApproveLoanRequestDialog';
import { AcceptLoanProposalDialog } from '@/components/AcceptLoanProposalDialog';

// Helper to safely access the JSON payload with a typed shape.
// The `payload` column stores arbitrary JSON; this cast gives us typed access
// to known keys like `item_name` and `rejection_reason`.
/**
 *
 * @param notification
 */
function getPayload(notification: NotificationItem) {
  return (notification.payload as Record<string, string | undefined>) || {};
}

/**
 *
 * @param notification
 */
function getTargetPath(notification: NotificationItem) {
  // Route all item/loan notifications to /my-items because an item can belong
  // to multiple groups — there is no single "correct" group to deep-link to.
  // Append ?item= so the target page can highlight/scroll to the relevant item.
  const payload = getPayload(notification);
  const itemId = payload.item_id;
  const itemParam = itemId ? `?item=${itemId}` : '';

  switch (notification.type) {
    case 'loan_request_received':
    case 'loan_request_approved':
    case 'loan_request_rejected':
    case 'loan_return_completed':
    case 'loan_return_initiated':
    case 'item_marked_overdue':
    case 'loan_due_date_set':
    case 'loan_proposal_received':
    case 'loan_proposal_accepted':
    case 'loan_proposal_declined':
    case 'loan_proposal_withdrawn':
      return `/my-items${itemParam}`;
    default:
      return '/dashboard';
  }
}

/**
 *
 * @param notification
 */
function getNotificationText(notification: NotificationItem) {
  const payload = getPayload(notification);
  const itemName = payload.item_name || 'an item';

  switch (notification.type) {
    case 'loan_request_received':
      return `New loan request received for ${itemName}.`;
    case 'loan_request_approved':
      return `Your loan request for ${itemName} was approved.`;
    case 'loan_request_rejected':
      return `Your loan request for ${itemName} was rejected.`;
    case 'item_marked_overdue':
      return `${itemName} has been marked overdue.`;
    case 'loan_return_completed':
      return `Loan return completed for ${itemName}.`;
    case 'loan_return_initiated':
      return `${itemName} has been marked as returned — please confirm.`;
    case 'loan_due_date_set': {
      const dueAt = payload.due_at;
      const formatted = dueAt
        ? new Date(dueAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })
        : 'a future date';
      return `A due date has been set for ${itemName}: ${formatted}.`;
    }
    case 'loan_proposal_received':
      return `${payload.lender_name || 'Someone'} wants to lend you ${itemName}. Tap to accept or decline.`;
    case 'loan_proposal_accepted':
      return `${payload.borrower_name || 'Someone'} has accepted your loan of ${itemName}.`;
    case 'loan_proposal_declined':
      return `${payload.borrower_name || 'Someone'} has declined your loan of ${itemName}.`;
    case 'loan_proposal_withdrawn':
      return `The loan offer for ${itemName} has been withdrawn.`;
    default:
      return 'You have a new notification.';
  }
}

/**
 *
 */
export function NotificationCenter() {
  const navigate = useNavigate();
  const { data: unreadCount = 0 } = useNotificationCount();
  const notificationsQuery = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const notifications = notificationsQuery.data?.pages.flat() ?? [];

  const [approvalRequestId, setApprovalRequestId] = useState<string | null>(null);
  const [approvalItemName, setApprovalItemName] = useState<string | undefined>(undefined);
  const [proposalLoanId, setProposalLoanId] = useState<string | null>(null);
  const [proposalItemName, setProposalItemName] = useState<string | undefined>(undefined);
  const [proposalLenderName, setProposalLenderName] = useState<string | undefined>(undefined);

  // Clicking a notification marks it as read and either opens an inline dialog
  // (for loan_request_received) or navigates to the relevant page.
  // Navigation/dialog open happens regardless of whether the mark-read call succeeds.
  const onOpenNotification = async (notification: NotificationItem) => {
    if (!notification.read_at) {
      try {
        await markRead.mutateAsync(notification.id);
      } catch {
        toast.error('Failed to mark notification as read.');
      }
    }
    const payload = getPayload(notification);
    if (notification.type === 'loan_request_received') {
      setApprovalRequestId(payload.loan_request_id ?? null);
      setApprovalItemName(payload.item_name);
    } else if (notification.type === 'loan_proposal_received') {
      setProposalLoanId(payload.loan_id ?? null);
      setProposalItemName(payload.item_name);
      setProposalLenderName(payload.lender_name);
    } else {
      navigate(getTargetPath(notification));
    }
  };

  const onMarkRead = async (notificationId: string) => {
    try {
      await markRead.mutateAsync(notificationId);
    } catch {
      toast.error('Failed to mark notification as read.');
    }
  };

  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <button
            style={{
              position: 'relative',
              width: 40,
              height: 40,
              borderRadius: RADIUS.sm,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: T.textSec,
            }}
            aria-label="Open notifications"
          >
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: 2,
                  right: 2,
                  minWidth: 18,
                  height: 18,
                  padding: '0 4px',
                  borderRadius: RADIUS.dot,
                  background: T.accent,
                  color: T.white,
                  fontSize: 10,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {unreadCount}
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[360px] p-0" align="end">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: `1px solid ${T.border}`,
              padding: '10px 14px',
            }}
          >
            <span style={{ fontSize: 15, fontWeight: 600, color: T.text }}>Notifications</span>
            <button
              onClick={() => markAllRead.mutate()}
              disabled={unreadCount === 0 || markAllRead.isPending}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '6px 10px',
                borderRadius: RADIUS.sm,
                background: 'none',
                border: 'none',
                fontSize: 12,
                fontWeight: 600,
                color: unreadCount === 0 || markAllRead.isPending ? T.textMuted : T.accent,
                cursor: unreadCount === 0 || markAllRead.isPending ? 'default' : 'pointer',
                fontFamily: 'inherit',
              }}
            >
              <CheckCheck className="h-4 w-4" />
              Mark all read
            </button>
          </div>

          <ScrollArea className="max-h-96">
            <div style={{ padding: 8 }}>
              {notifications.length === 0 && (
                <p style={{ padding: 16, fontSize: 13, color: T.textMuted }}>
                  No notifications yet.
                </p>
              )}

              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  style={{
                    marginBottom: 4,
                    borderRadius: RADIUS.sm,
                    border: `1px solid ${T.border}`,
                    borderLeft: notification.read_at
                      ? `1px solid ${T.border}`
                      : `3px solid ${T.accent}`,
                    padding: 12,
                    background: T.surface,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => onOpenNotification(notification)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 8,
                      }}
                    >
                      <p style={{ fontSize: 14, color: T.text, margin: 0 }}>
                        {getNotificationText(notification)}
                      </p>
                    </div>
                    <p style={{ marginTop: 4, fontSize: 12, color: T.textMuted }}>
                      {new Date(notification.created_at).toLocaleString()}
                    </p>
                  </button>
                  {notification.type === 'loan_request_rejected' &&
                    getPayload(notification).rejection_reason && (
                      <p style={{ marginTop: 4, fontSize: 11, color: T.danger }}>
                        Reason: {getPayload(notification).rejection_reason}
                      </p>
                    )}
                  {!notification.read_at && (
                    <button
                      type="button"
                      onClick={() => onMarkRead(notification.id)}
                      style={{
                        marginTop: 6,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        padding: '4px 8px',
                        borderRadius: RADIUS.sm,
                        background: 'none',
                        border: 'none',
                        fontSize: 11,
                        fontWeight: 600,
                        color: T.textMuted,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      <Check className="h-3 w-3" />
                      Mark as read
                    </button>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>

          {notificationsQuery.hasNextPage && (
            <div style={{ borderTop: `1px solid ${T.border}`, padding: 8 }}>
              <button
                onClick={() => notificationsQuery.fetchNextPage()}
                disabled={notificationsQuery.isFetchingNextPage}
                style={{
                  width: '100%',
                  padding: '9px',
                  borderRadius: RADIUS.sm,
                  background: T.surfaceSubtle,
                  color: T.textSec,
                  border: `1px solid ${T.border}`,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: notificationsQuery.isFetchingNextPage ? 'default' : 'pointer',
                  fontFamily: 'inherit',
                  opacity: notificationsQuery.isFetchingNextPage ? 0.6 : 1,
                }}
              >
                Load more
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>

      <ApproveLoanRequestDialog
        requestId={approvalRequestId}
        itemName={approvalItemName}
        open={approvalRequestId !== null}
        onOpenChange={(open) => {
          if (!open) setApprovalRequestId(null);
        }}
      />

      <AcceptLoanProposalDialog
        loanId={proposalLoanId}
        itemName={proposalItemName}
        lenderName={proposalLenderName}
        open={proposalLoanId !== null}
        onOpenChange={(open) => {
          if (!open) setProposalLoanId(null);
        }}
      />
    </>
  );
}
